import _ from 'lodash';

class ProcessConverter {


    // Used to convert from one format to the other as needed depending on 
    // which fromat it was already in
    toggleFormat(process, togglingFromOld) {
        if (togglingFromOld) {
            return this.oldToNewProcess(process);
        } else {
            return this.newToOldProcess(process);
        }
    }

    /**************************************************
     * CONVERTING OLD FORMAT TO NEW
     **************************************************/

    oldToNewProcess(oldProcess) {
        // Get the proceses that have a description
        //  select * from processes where proc_desc is not null and proc_type_id=5;
        const process = {};

        // Process General Information
        let props = this.getSafeProperty('props', oldProcess);
        if (!props) {
            props = {};
        }
        this.transferProperty(oldProcess, process, 'name');
        this.transferProperty(oldProcess, process, 'type');
        this.transferProperty(oldProcess, process, 'desc', 'description');

        this.transferProperty(props, process, 'processing_interval');
        // convert all old VAP's processing intervals to default to 1 day (86400 secs) if the value is blank or null
        if (process.type === 'VAP' && (process.processing_interval === null || process.processing_interval === '')) {
            process.processing_interval = '86400';
        }

        // ui_state
        this.transferProperty(props, process, 'ui_state');

        // New:  Add UI State so user can reference last run information
        let uiState = this.getSafeProperty('ui_state', props);
        if (uiState) {
            process.ui_state = uiState;
        }

        // notification email
        const warnings = this.getSafeProperty('warning_mail', props) !== null;
        const errors = this.getSafeProperty('error_mail', props) !== null;
        let notificationEmail = this.getSafeProperty('error_mail', props);
        if (!notificationEmail) {
            notificationEmail = this.getSafeProperty('warning_mail', props);
        }
        if (notificationEmail === null) {
            notificationEmail = '';
        }
        process.notification_email = notificationEmail;
        process.email_errors = errors;
        process.email_warnings = warnings;

        // Run Locations
        const runLocations = [];
        const oldRunLocations = this.getSafeProperty('locations', oldProcess);
        for (let locationIndex in oldRunLocations) {
            let location = _.cloneDeep(oldRunLocations[locationIndex]);
            // The format we get as input for the PERL scripts, separates a location into
            // site and facility properties
            if (location.site) {
                // convert site to upper case
                const newSite = location.site.toUpperCase();
                oldRunLocations[locationIndex].site = newSite;
                runLocations.push(oldRunLocations[locationIndex]);
            } else {
                // A location is simply a string with the site and facility separated
                // by a whitespace character.
                const locationPieces = location.split(/\s/);
                runLocations.push(
                    {
                        site: locationPieces[0].toUpperCase(),
                        fac: locationPieces[1]
                    }
                );
            }
        }
        process.run_locations = runLocations;

        // Input Datastreams
        const inputDatastreams = [];
        const oldInputs = this.getSafeProperty('inputs', oldProcess);
        const oldRetrieval = this.getSafeProperty('ret', oldProcess);
        // Add the original input datastreams
        if (oldInputs !== null) {
            for (let inputIndex in oldInputs) {
                const inputstream = oldInputs[inputIndex];
                inputDatastreams.push(inputstream);
            }
        }
        // Add the input datastreams from the retrieval
        if (!_.isEmpty(oldRetrieval)) {
            const queries = this.getSafeProperty(['queries'], oldRetrieval);
            for (let datasetName in queries) { // as datasetName => datasetRules)
                const datasetRules = queries[datasetName];
                for (let datasetRuleIndex in datasetRules) {
                    const datasetRule = datasetRules[datasetRuleIndex];
                    const datastreamName = `${this.getSafeProperty('class', datasetRule)}.${this.getSafeProperty('level', datasetRule)}`;
                    if (!(inputDatastreams.join(', ').includes(datastreamName))) {
                        inputDatastreams.push(datastreamName);
                    }
                }
            }
        }
        inputDatastreams.sort(); // sort in alphabetical order
        process.input_datastreams = inputDatastreams;

        // Output Datastreams
        const outputDatastreams = this.getSafeProperty('outputs', oldProcess);
        outputDatastreams.sort(); // sort in alphabetical order
        process.output_datastreams = outputDatastreams;

        const outputInterval = this.getSafeProperty('output_interval', props);
        let outputIntervalList = [];
        if (outputInterval !== null) {
            outputIntervalList = outputInterval.split(',');
        }

        process.output_interval = {};
        outputDatastreams.forEach(outputDatastreamName => {
            process.output_interval[outputDatastreamName] = { interval: null, timezone: null };
        });

        outputIntervalList.forEach(intervalString => {
            const intervalValues = intervalString.split('-');

            const intDatastreamName = intervalValues[0];
            const intValue = intervalValues[1] ? intervalValues[1] : null;
            const intTimezone = intervalValues[2] ? intervalValues[2] : null;

            if (process.output_interval[intDatastreamName]) {
                process.output_interval[intDatastreamName].interval = intValue;
                process.output_interval[intDatastreamName].timezone = intTimezone;
            }
        });


        // Variable Retrieval
        let newRetrieval = this.getNewRetrieval(oldProcess);
        if (newRetrieval === null && inputDatastreams.length === 0 && process.type === 'VAP') {
            // If the user saved an empty process with neither input streams or retrieval parameters defined, and the
            // process is a VAP, we assume we are using ADI, so we must define a retrieval structure
            newRetrieval = {
                input_datasets: {},
                retrieved_variables: {},
                coordinate_systems: {},
                output_datastream_variable_mappings: {},
                transformation_parameters: {}
            };
        }
        process.variable_retrieval = newRetrieval;

        return process;
    }

    getUniqueVariableName(variableName, fieldNames) {
        const index = fieldNames[variableName];
        return `${variableName}_${index}`;
    }

    getNewRetrieval(oldProcess) {
        // ret_datastreams table contains the input dataset information for VAPs
        // process type 5 is VAP
        //  select * from processes where proc_name='radfluxbrs1long' and proc_type_id=5;
        // proc_id = 1538
        let retrieval = null;
        const oldRetrieval = this.getSafeProperty('ret', oldProcess);

        if (!_.isEmpty(oldRetrieval)) {
            retrieval = {};

            // First check if this process has subgroups.  If it does, then throw an exception as subgroups are
            // not currently supported.
            // TODO: move this to subroutine with variable passed by reference since it's getting ugly
            const groups = this.getSafeProperty('groups', oldRetrieval);
            const queries = this.getSafeProperty('queries', oldRetrieval);

            let hasSubgroups = false;
            for (let groupName in groups) {
                const subGroupsList = groups[groupName];
                if (subGroupsList.length > 1) {
                    hasSubgroups = true;
                    break;

                } else {
                    // If the subgroup name is not the same as the group name, then we need to fix it to be the same.
                    // This is for backwards compatibility with some old formats that have changed.
                    if (subGroupsList[0] !== groupName) {
                        const subGroupName = subGroupsList[0];

                        if (subGroupName in groups) {
                            // Move the group to match the subgroup name
                            // We have to set it directly on the $oldRetrieval object because PHP makes copies of the objects
                            // inside if and for loops :(
                            oldRetrieval.groups[subGroupName] = subGroupsList;
                        }
                        delete oldRetrieval.groups[groupName]; // get rid of the old group

                        // If any fields were pointing to the $groupName, we have to move them to the subgroupName
                        oldRetrieval.fields.forEach((field, index) => {
                            if (field.group === groupName) {
                                oldRetrieval.fields[index].group = subGroupName;
                            }
                        });
                    }
                }
            }

            // Now check if the groups or queries have space in the name.  If they do, then replace space with underscore
            // TODO: refactor this code to use method call since it is redundant
            for (let groupName in groups) {

                if (groupName.match(/\s/g)) {
                    const newName = groupName.replace(/\s/g, '_');
                    groups[newName] = groups[groupName];
                    const oldNameIndex = groups.findIndex(x => x === groupName);
                    groups.splice(oldNameIndex, 1);
                    groups[newName][0] = newName; // make sure the subgroup name is changed as well
                }
            }

            for (let queryName in queries) {

                // if ( preg_match('/\s/',queryName) ) {
                if (queryName.match(/\s/g)) {
                    const newName = queryName.replace(/\s/g, '_');
                    queries[newName] = queries[queryName];
                    const oldNameIndex = queries.findIndex(x => x === queryName);
                    queries.splice(oldNameIndex, 1);
                }
            }

            // Now check if there are duplicate retrieved variable names
            // It could happen in old processes that there are multiple variables with the same name.  If that is the
            // case, then we need to rename the duplicates to a new name!
            const fieldNames = {};
            if (oldRetrieval.fields) {
                for (let i = 0; i < oldRetrieval.fields.length; i++) {
                    const fieldName = oldRetrieval.fields[i].name;
                    let newName = fieldName;

                    if (fieldName in fieldNames) {
                        // de-dupe the name
                        newName = this.getUniqueVariableName(fieldName, fieldNames);
                        oldRetrieval.fields[i].name = newName;
                        fieldNames[newName] = 1;
                        fieldNames[fieldName] = fieldNames[fieldName] + 1;

                    } else {
                        fieldNames[newName] = 1;
                    }

                    // We have to fix spaces in the group name of the retrieved variables as well
                    const fieldGroupName = oldRetrieval.fields[i].group;
                    if (fieldGroupName.match(/\s/g)) {
                        let newFieldGroupName = fieldGroupName.replace(/\s/g, '_');
                        oldRetrieval.fields[i].group = newFieldGroupName;
                    }
                }
            }


            if (hasSubgroups) {
                console.log('Oh god this has subgroups!!! ABORT!');
                // throw new \Exception("Unable to open process {processName} because it contains subgroups and they are currently not supported in the UI.");
            }

            // Input Datasets
            const inputDatasets = this.getNewInputDatasets(oldRetrieval);
            retrieval.input_datasets = inputDatasets;

            // Retrieved Variables
            const retrievedVariables = this.getNewRetrievedVariables(oldRetrieval);
            retrieval.retrieved_variables = retrievedVariables;

            // Coordinate Systems
            const coordinateSystems = this.getNewCoordinateSystems(oldRetrieval);
            retrieval.coordinate_systems = coordinateSystems;

            // Output Datastreams Variable Mapping
            const outputDatastreamVariableMappings = this.getNewOutputDatastreamVariableMappings(oldRetrieval);
            retrieval.output_datastream_variable_mappings = outputDatastreamVariableMappings;

            // Add the transform parameters
            this.transferProperty(oldRetrieval, retrieval, 'transformation_parameters');

        }
        return retrieval;
    }

    getNewOutputDatastreamVariableMappings(oldRetrieval) {
        // Map each output datastream to the variable mappings
        // From the ret{} get the "out" array for each field.  Each object in the array maps an output datastream variable
        // to the retrieved variable
        let fields = this.getSafeProperty('fields', oldRetrieval);
        const mappedVariables = {};

        // Loop through the retrieved variables
        for (let fieldIndex in fields) {
            const field = fields[fieldIndex];
            const fieldVariableName = this.getSafeProperty('name', field);
            const outs = this.getSafeProperty('out', field);
            /*"out": [
             {
             "d": "radfluxbrs1long.c1",
             "f": "downwelling_shortwave"
             }
             ],*/

            for (let outIndex in outs) {
                const out = outs[outIndex];
                const datastreamName = out.d;
                const datastreamVariable = out.f;
                const retrievedVariableName = fieldVariableName;

                if (mappedVariables[datastreamName] === undefined) {
                    mappedVariables[datastreamName] = {};
                }
                mappedVariables[datastreamName][retrievedVariableName] = datastreamVariable;
            }
        }

        // If array is empty, cast as an object so it will be serialized in the json as {} instead of []
        // if(mappedVariables.length != 0) {
        //     // sort the keys so they are in alphabetical order
        //     mappedVariables.sort();
        // }
        return mappedVariables;
    }

    getNewInputDatasets(oldRetrieval) {
        const inputDatasets = {};
        const queries = this.getSafeProperty(['queries'], oldRetrieval);

        for (let datasetName in queries) {
            const datasetRules = queries[datasetName];
            const rules = [];
            let offsets = [0, 0];
            const inputDataset = {
                name: datasetName
            };

            // First get the rules
            for (let datasetRuleIndex in datasetRules) {
                const datasetRule = datasetRules[datasetRuleIndex];
                const priority = this.getSafeProperty('order', datasetRule);
                const datastreamName = `${this.getSafeProperty('class', datasetRule)}.${this.getSafeProperty('level', datasetRule)}`;

                // Run Location
                // If site and facility are null, then this translates to "Run Location" in the UI
                const runLocation = {
                    site: this.getSafeProperty('sdep', datasetRule),
                    fac: this.getSafeProperty('fdep', datasetRule)
                };

                // Run Time Start
                // 5 processes have a begin_date_dependency.  3 of those have an end_date_dependency
                // Test time dependency on mwrret
                let runTimeStart = this.getSafeProperty('tdep0', datasetRule);
                if (runTimeStart === null) {
                    runTimeStart = 'ANY_TIME';
                }

                // Run Time End
                let runTimeEnd = this.getSafeProperty('tdep1', datasetRule);
                if (runTimeEnd === null) {
                    runTimeEnd = 'ANY_TIME';
                }

                const runTime = {
                    start: runTimeStart,
                    end: runTimeEnd
                };

                // data location (where to retrieve data from)
                // Test data location on aeriprof
                const dataLocation = {
                    site: this.getSafeProperty('site', datasetRule),
                    fac: this.getSafeProperty('fac', datasetRule)
                };

                if (runLocation.site === null) {
                    runLocation.site = 'RUN_LOCATION';
                } else {
                    runLocation.site = runLocation.site.toUpperCase();
                }

                if (dataLocation.site === null) {
                    dataLocation.site = 'RUN_LOCATION';
                } else {
                    dataLocation.site = dataLocation.site.toUpperCase();
                }

                const rule = {
                    priority,
                    run_location: runLocation,
                    run_time: runTime,
                    data_location: dataLocation,
                    datastream_name: datastreamName,
                    variable_mappings: {}
                };
                rules.push(rule);
                // array_push(rules, rule);
            }
            inputDataset.rules = rules;

            // Then get the input datastream variable mappings
            // We need to loop through all the fields in the ret{} object and pull out the "vars" array.
            // There should be EXACTLY the same number of rules in the input dataset as there are "vars".
            // For each var, get the rule at the same index so we can find out the input datastream
            // Then we map the field name (ret var name) a map of input datastream to the variable name
            const fields = this.getSafeProperty('fields', oldRetrieval);

            // Loop through the retrieved variables
            for (let fieldIndex in fields) {
                const field = fields[fieldIndex];
                // Make sure this variable is mapped to the current datasetName
                const fieldVariableName = this.getSafeProperty('name', field);
                const fieldDatasetName = this.getSafeProperty('group', field);
                if (fieldDatasetName !== datasetName) {
                    continue;
                }

                // If so, then get the offsets
                // NOTE: field offsets may be different even though they are coming from the same data source.)\
                // In this case, we pick the largest start and largest end offset to represent the offsets for this
                // input dataset, since that is what will be used by the retriever.
                /*"window": [
                 7776000,
                 0
                 ],*/

                const oldOffsets = this.getSafeProperty('window', field);
                if (oldOffsets !== null) {
                    for (let i = 0; i <= 1; i++) {
                        if (oldOffsets[i] > offsets[i]) {
                            // We always pick the largest bounds for the offsets
                            offsets[i] = oldOffsets[i];
                        }
                    }
                }

                // Also get the variable mappings
                const vars = this.getSafeProperty('vars', field);
                /*"vars": [
                 [
                 "down_long_hemisp" // mappings for rule 0
                 ],
                 [
                 "down_long_hemisp" // mappings for rule 1
                 ]
                 ],*/

                for (let i = 0; i < vars.length; i++) {
                    // If we have more vars than rules, this means that the process has subgroups.  (Currently there
                    // is only one process where this is the case.)  For this case, we are just omitting the extra
                    // vars because they happen to be identical
                    if (i < rules.length) {
                        rules[i].variable_mappings[fieldVariableName] = vars[i];
                    }
                }
            }

            if (offsets === null) {
                offsets = [0, 0];
            } else {
                if (!offsets[0] || offsets[0] < 0) {
                    offsets[0] = 0;
                }
                if (!offsets[1] || offsets[1] < 0) {
                    offsets[1] = 0;
                }
            }

            inputDataset.offsets = offsets;

            inputDatasets[datasetName] = inputDataset;
        }

        // sort the datasets so they are in alphabetical order
        // inputDatasets.sort();
        return inputDatasets;
    }

    getNewRetrievedVariables(oldRetrieval) {
        const retrievedVariables = {};
        const fields = this.getSafeProperty(['fields'], oldRetrieval);

        for (let fieldIndex in fields) {
            const field = fields[fieldIndex];
            const retrievedVariable = {};

            // name
            const variableName = this.getSafeProperty('name', field);
            this.transferProperty(field, retrievedVariable, 'name');

            // data type
            this.transferProperty(field, retrievedVariable, 'dtype', 'data_type');

            // units
            let units = this.getSafeProperty('units', field);
            if (units === null || units === '') { //zoe made this change to normalize default unit value
                units = 'SAME_AS_INPUT_DATASTREAM';
            }
            retrievedVariable.units = units;

            // dims
            this.transferProperty(field, retrievedVariable, 'dims');

            // is variable required?
            this.transferProperty(field, retrievedVariable, 'reqd', 'variable_required');

            // should companion qc variable be retrieved?
            this.transferProperty(field, retrievedVariable, 'qc', 'retrieve_qc_variable');

            // is qc variable required?
            this.transferProperty(field, retrievedVariable, 'qcreqd', 'qc_variable_required');

            // input dataset
            this.transferProperty(field, retrievedVariable, 'group', 'input_dataset');

            // coordinate system
            let coordinateSystem = this.getSafeProperty('shape', field);
            if (coordinateSystem === '') {
                coordinateSystem = null;
            }
            retrievedVariable.coordinate_system = coordinateSystem;

            // dims - this defaults to the dimensions of the variable retrieved from the input stream,
            // but this could be changed by the process UI once the variable has been assigned a coordinate system.
            // The dims on the variable can be a subset of the dims on the coordinate system, but any dim on the variable
            // must be present in the associated coordinate system.
            this.transferProperty(field, retrievedVariable, 'dims', 'dims');

            retrievedVariables[variableName] = retrievedVariable;
        }
        return retrievedVariables;
    }

    getNewCoordinateSystems(oldRetrieval) {
        const shapes = this.getSafeProperty(['shapes'], oldRetrieval);
        const coordinateSystems = {};

        for (let coordinateSystemName in shapes) {
            const oldDimensions = shapes[coordinateSystemName];
            const coordinateSystem = {
                name: coordinateSystemName
            };
            const dimensions = {};

            // dimensions
            for (let index in oldDimensions) {
                const oldDimension = oldDimensions[index];
                const dimensionName = this.getSafeProperty('name', oldDimension);
                let dataType = this.getSafeProperty('dtype', oldDimension); // null means 'same as input datastream'
                if (dataType === null) {
                    dataType = 'SAME_AS_RETRIEVED_VARIABLE';
                }
                let units = this.getSafeProperty('units', oldDimension);  // null means 'same as input datastream'
                if (units === null) {
                    units = 'SAME_AS_RETRIEVED_VARIABLE';
                }
                const dimension = {
                    name: dimensionName,
                    index: parseInt(index, 10),
                    coordinate_variable_data_type: dataType,
                    coordinate_variable_units: units
                };

                // For backwards compatibility, if the old trange parameter is set, we need to forward it through
                // to the UI so that it can be properly converted into the range transformation parameter.
                const trange = this.getSafeProperty('trange', oldDimension);
                if (trange) {
                    dimension.trange= trange;
                }

                const oldTType = this.getSafeProperty('ttype', oldDimension);
                let transformMethod = oldTType;
                const query = this.getSafeProperty('query', oldDimension);
                let binAlignment = this.getSafeProperty('talign', oldDimension);
                const length = this.getSafeProperty('length', oldDimension);
                const start = this.getSafeProperty('start', oldDimension);
                const interval = this.getSafeProperty('interval', oldDimension);

                if (binAlignment === -1) {
                    binAlignment = 'START';
                } else if (binAlignment === 1) {
                    binAlignment = 'END';
                } else if (binAlignment === 0) {
                    binAlignment = 'MIDDLE';
                }

                if (transformMethod === null) {
                    transformMethod = 'AUTO'; // mapping grid will have non-null query value
                }

                const dataTransformation = {
                    transformation_type: transformMethod
                };
                if (binAlignment !== null) {
                    dataTransformation.bin_alignment = binAlignment;
                }

                if (query !== null) { // mapping
                    // Example process that uses mapping: http://pcm-server.local/app_dev.php/processes/tutorialcs

                    // mapping
                    dimension.gridding_method = 'mapping';
                    dimension.input_dataset = query;
                    dimension.coordinate_variables_per_rule = this.getSafeProperty('vars', oldDimension);

                } else if (interval === null && length === null && start === null) {  // set at runtime
                    dimension.gridding_method = 'set_at_runtime';

                } else { // regular grid

                    // test pbl for a process with regular grid and almost nothing set
                    dimension.gridding_method = 'regular_grid';
                    dataTransformation.length = parseInt(length, 10);
                    dataTransformation.start = parseInt(start, 10);
                    dataTransformation.interval = parseInt(interval, 10);

                    // End = start + (binLength * (#bins - 1))
                    dataTransformation.end = dataTransformation.start +
                        (dataTransformation.interval * (dataTransformation.length - 1));
                }

                dimension.data_transformation = dataTransformation;

                dimensions[dimensionName] = dimension;

            }
            coordinateSystem.dimensions = dimensions;
            coordinateSystems[coordinateSystemName] = coordinateSystem;
        }
        return coordinateSystems;
    }


    /**************************************************
     * CONVERTING NEW FORMAT TO OLD
     **************************************************/

    newToOldProcess(newProcess) {
        const oldProcess = {};

        // Transfer the top-level props
        let category = 'VAP';
        const type = this.getSafeProperty('type', newProcess);
        if (type === 'Ingest') {
            category = 'Instrument';
        }
        oldProcess.category = category;

        this.transferProperty(newProcess, oldProcess, 'name');
        this.transferProperty(newProcess, oldProcess, 'type');
        this.transferProperty(newProcess, oldProcess, 'description', 'desc');
        this.transferProperty(newProcess, oldProcess, 'run_locations', 'locations');

        // Run Locations - 'site' MUST be all lowercase or the save will fail!
        const runLocations = [];
        const newRunLocations = this.getSafeProperty('run_locations', newProcess);
        for (let index in newRunLocations) {
            // convert site to lower case
            const location = _.cloneDeep(newRunLocations[index]);
            location.site = location.site.toLowerCase();
            runLocations.push(location);
        }
        oldProcess.locations = runLocations;

        // Props
        let email = this.getSafeProperty('notification_email', newProcess);
        if (email === '') {
            email = null;
        }
        let warningMail = null;
        let errorMail = null;
        if (email && this.getPropertyAsBoolean('email_errors', newProcess)) {
            errorMail = email;
        }
        if (email && this.getPropertyAsBoolean('email_warnings', newProcess)) {
            warningMail = email;
        }

        let uiState = this.getSafeProperty('ui_state', newProcess);
        if (!uiState) {
            uiState = null;
        }

        const props = {
            warning_mail: warningMail,
            error_mail: errorMail,
            ui_state: uiState
        };

        // Only set the processing_interval if this process has a retriever
        const variableRetrieval = this.getSafeProperty('variable_retrieval', newProcess);
        if (variableRetrieval) {
            props.processing_interval = this.getSafeProperty('processing_interval', newProcess);
        }

        let outputInterval = null;
        const intervalList = [];
        Object.keys(newProcess.output_interval).forEach(datastreamName => {
            const intervalObj = newProcess.output_interval[datastreamName];
            const interval = intervalObj.interval;
            const timezone = intervalObj.timezone;

            if (interval) {
                let intString = `${datastreamName}-${interval}`;
                if (timezone) {
                    intString = `${intString}-${timezone}`;
                }
                intervalList.push(intString);
            }
        });

        if (intervalList.length > 0) {
            outputInterval = intervalList.join(',');
        }

        props.output_interval = outputInterval;

        oldProcess.props = props;

        // Input Datastreams (only add them if not empty)
        if (!_.isEmpty(newProcess.input_datastreams)) {
            this.transferProperty(newProcess, oldProcess, 'input_datastreams', 'inputs');
        }

        // Output Datastreams
        this.transferProperty(newProcess, oldProcess, 'output_datastreams', 'outputs');

        // Variable Retrieval
        const ret = this.getOldRetrieval(newProcess);
        if (ret.length !== 0) {
            oldProcess.ret = ret;
        }

        return oldProcess;
    }

    getOldRetrieval(newProcess) {
        const newRetrieval = this.getSafeProperty('variable_retrieval', newProcess);
        const oldRetrieval = {};

        if (newRetrieval !== null) {

            // Add the retrieved variables (fields), with input and output variable mappings
            this.writeOldRetrievedVariables(newRetrieval, oldRetrieval);

            // Add the input datasets (groups and queries)
            this.writeOldQueriesAndGroups(newRetrieval, oldRetrieval);

            // Add the coordinate systems (shapes)
            this.writeOldShapes(newRetrieval, oldRetrieval);

            // Add the transform parameters
            this.transferProperty(newRetrieval, oldRetrieval, 'transformation_parameters');

        }
        return oldRetrieval;
    }

    writeOldRetrievedVariables(newRetrieval, oldRetrieval) {
        const fields = [];
        const retrievedVariables = this.getSafeProperty('retrieved_variables', newRetrieval);
        const inputDatasets = this.getSafeProperty('input_datasets', newRetrieval);
        const outputDatastreamMappings = this.getSafeProperty('output_datastream_variable_mappings', newRetrieval);

        for (let retrievedVariableName in retrievedVariables) {
            let offsets = null;
            const field = {};
            const retrievedVariable = retrievedVariables[retrievedVariableName];
            const group = this.getSafeProperty('input_dataset', retrievedVariable);
            const variableName = retrievedVariable.name;

            // General properties
            field.name = variableName; // name
            this.transferProperty(retrievedVariable, field, 'data_type', 'dtype'); // data type
            this.transferProperty(retrievedVariable, field, 'variable_required', 'reqd'); // is variable required?
            this.transferProperty(retrievedVariable, field, 'retrieve_qc_variable', 'qc'); // should companion qc variable be retrieved?
            this.transferProperty(retrievedVariable, field, 'qc_variable_required', 'qcreqd'); // is qc variable required?
            this.transferProperty(retrievedVariable, field, 'input_dataset', 'group'); // input dataset
            this.transferProperty(retrievedVariable, field, 'dims', 'dims');

            // coordinate system
            let coordinateSystem = this.getSafeProperty('coordinate_system', retrievedVariable);
            field.shape = coordinateSystem;

            // units
            let units = this.getSafeProperty('units', retrievedVariable);
            if (units === 'SAME_AS_INPUT_DATASTREAM') {
                units = '';
            }
            field.units = units;

            // Input stream variable mappings & offsets
            const vars = [];
            for (const datasetName in  inputDatasets) {
                const inputDataset = inputDatasets[datasetName];
                if (datasetName === group) {
                    offsets = this.getSafeProperty('offsets', inputDataset);

                    const rules = this.getSafeProperty('rules', inputDataset);
                    if (rules !== undefined) {
                        for (let index in rules) {
                            const rule = rules[index];
                            vars[index] = rule.variable_mappings[variableName];
                        }
                    }
                }
            }
            field.vars = vars;

            // Offsets
            if (offsets !== null) {
                if (!offsets[0]) {
                    offsets[0] = 0;
                }
                if (!offsets[1]) {
                    offsets[1] = 0;
                }
                field.window = offsets;
            }

            // Output stream variable mappings
            const out = [];
            for (let datastreamName in outputDatastreamMappings) {
                const variablesMap = outputDatastreamMappings[datastreamName]
                for (let variableName in variablesMap) {
                    const outputStreamVariableName = variablesMap[variableName];
                    if (variableName === retrievedVariableName) {
                        const mapping = {
                            d: datastreamName,
                            f: outputStreamVariableName
                        };
                        out.push(mapping);
                    }
                }
            }
            field.out = out;
            fields.push(field);
        }

        oldRetrieval.fields = fields;
    }

    writeOldShapes(newRetrieval, oldRetrieval) {
        const shapes = {};
        const coordinateSystems = this.getSafeProperty('coordinate_systems', newRetrieval);

        for (let coordinateSystemName in coordinateSystems) {
            let coordinateSystem = coordinateSystems[coordinateSystemName];
            const dimensions = [];
            const newDimensions = this.getSafeProperty('dimensions', coordinateSystem);

            // dimensions
            for (let dimensionName in newDimensions) {
                const newDimension = newDimensions[dimensionName];
                let dtype = this.getSafeProperty('coordinate_variable_data_type', newDimension);
                if (dtype === 'SAME_AS_RETRIEVED_VARIABLE') {
                    dtype = null;
                }
                let units = this.getSafeProperty('coordinate_variable_units', newDimension);
                if (units === 'SAME_AS_RETRIEVED_VARIABLE') {
                    units = null;
                }

                // We are no longer storing trange, as this transformation parameter needs to be stored with the
                // variable. Users will need to specify range in the transformation_parameters.
                let talign = null;
                let query = null;
                let name = dimensionName;
                let vars = [];
                let interval = null;
                let length = null;
                let ttype = null;
                let start = null;

                let griddingMethod = this.getSafeProperty('gridding_method', newDimension);

                // Data transformation parameters
                let dataTransformation = this.getSafeProperty('data_transformation', newDimension);

                ttype = this.getSafeProperty('transformation_type', dataTransformation);
                if (ttype === 'AUTO') {
                    ttype = null;
                }

                if (griddingMethod === 'mapping') {
                    query = this.getSafeProperty('input_dataset', newDimension);
                    vars = this.getSafeProperty('coordinate_variables_per_rule', newDimension);

                } else if (griddingMethod !== 'set_at_runtime') {
                    interval = this.getSafeProperty('interval', dataTransformation);
                    length = this.getSafeProperty('length', dataTransformation);
                    start = this.getSafeProperty('start', dataTransformation);
                }

                talign = this.getSafeProperty('bin_alignment', dataTransformation);
                if (talign === 'START') {
                    talign = -1;
                } else if (talign === 'END') {
                    talign = 1;
                } else if (talign === 'MIDDLE') {
                    talign = 0;
                }

                const dimension = {
                    query,
                    name,
                    vars,
                    interval,
                    dtype,
                    length,
                    ttype,
                    start,
                    units
                };
                if (talign !== null) {
                    dimension.talign = talign;
                }

                // For backwards compatibility - if user enters this in the text editor, we will keep it so that
                // ProcessUtils.fixProcessModel will properly convert it to the range transformation parameter.
                const trange = this.getSafeProperty('trange', newDimension);
                if (trange) {
                    dimension.trange = trange;
                }

                const index = parseInt(this.getSafeProperty('index', newDimension), 10);
                dimensions[index] = dimension;

            }
            shapes[coordinateSystemName] = dimensions;
        }

        oldRetrieval.shapes = shapes;
    }

    writeOldQueriesAndGroups(newRetrieval, oldRetrieval) {
        const groups = {};
        const queries = {};

        // Groups
        const inputDatasets = this.getSafeProperty('input_datasets', newRetrieval);

        for (let datasetName in inputDatasets) {
            const inputDataset = inputDatasets[datasetName];
            // write the groups
            groups[datasetName] = [datasetName];

            // write the queries
            queries[datasetName] = [];
            const newRules = this.getSafeProperty('rules', inputDataset);
            for (let index in newRules) {
                const newRule = newRules[index];
                const order = this.getSafeProperty('priority', newRule);
                let tdep0 = this.getSafeProperty(['run_time', 'start'], newRule);
                if (tdep0 === 'ANY_TIME') {
                    tdep0 = null;
                }
                let tdep1 = this.getSafeProperty(['run_time', 'end'], newRule);
                if (tdep1 === 'ANY_TIME') {
                    tdep1 = null;
                }

                const dodName = this.getSafeProperty('datastream_name', newRule);
                const namePieces = dodName.split('.');
                const className = namePieces[0];
                const dataLevel = namePieces[1];

                let sdep = this.getSafeProperty(['run_location', 'site'], newRule);
                if (sdep === 'RUN_LOCATION') {
                    sdep = null;
                } else {
                    sdep = sdep.toLowerCase();
                }
                const fdep = this.getSafeProperty(['run_location', 'fac'], newRule);

                let site = this.getSafeProperty(['data_location', 'site'], newRule);
                if (site === 'RUN_LOCATION') {
                    site = null;
                } else {
                    site = site.toLowerCase();
                }
                const fac = this.getSafeProperty(['data_location', 'fac'], newRule);

                const oldRule = {
                    tdep1,
                    tdep0,
                    level: dataLevel,
                    site,
                    order,
                    fac,
                    class: className,
                    sdep,
                    fdep
                };

                queries[datasetName].push(oldRule);
            }
        }

        oldRetrieval.groups = groups;
        oldRetrieval.queries = queries;
    }


    /**************************************************
     * UTILITY CONVERSION FUNCTIONS
     **************************************************/
    /**
     * Safely transfer a property from one object to another
     * @param sourceObj
     * @param destObj
     * @param oldName
     * @param newName
     */
    transferProperty(sourceObj, destObj, oldName, newName = null) {
        const name = newName ? newName : oldName;
        destObj[name] = this.getSafeProperty(oldName, sourceObj);
    }

    /**
     * Gets a property from the object, returning null if the key does not exist.
     * @param name
     * @param obj
     * @return object|null
     */
    getSafeProperty(names, obj) {
        let value = null;

        if (Array.isArray(names)) {
            const namesArray = names;
            let currentObj = obj;
            const length = namesArray.length;

            for (let i = 0; i < length; i++) {
                const name = namesArray[i];
                if (currentObj && (name in currentObj)) {
                    if (i === length - 1) {
                        value = currentObj[name];
                    } else {
                        currentObj = currentObj[name];
                    }
                }
            }


        } else {
            if (names in obj) {
                value = obj[names];
            }
        }
        return value;
    }

    getPropertyAsBoolean(names, obj) {
        const value = this.getSafeProperty(names, obj);
        return this.boolval(value);
    }

    // From http://locutus.io/php/var/boolval/
    boolval(mixedVar) {
        // original by: Will Rowe
        //   example 1: boolval(true)
        //   returns 1: true
        //   example 2: boolval(false)
        //   returns 2: false
        //   example 3: boolval(0)
        //   returns 3: false
        //   example 4: boolval(0.0)
        //   returns 4: false
        //   example 5: boolval('')
        //   returns 5: false
        //   example 6: boolval('0')
        //   returns 6: false
        //   example 7: boolval([])
        //   returns 7: false
        //   example 8: boolval('')
        //   returns 8: false
        //   example 9: boolval(null)
        //   returns 9: false
        //   example 10: boolval(undefined)
        //   returns 10: false
        //   example 11: boolval('true')
        //   returns 11: true
        if (mixedVar === false) {
            return false;
        }
        if (mixedVar === 0 || mixedVar === 0.0) {
            return false;
        }
        if (mixedVar === '' || mixedVar === '0') {
            return false;
        }
        if (Array.isArray(mixedVar) && mixedVar.length === 0) {
            return false;
        }
        if (mixedVar === null || mixedVar === undefined) {
            return false;
        }
        return true;
    }
}


export default new ProcessConverter();



// WEBPACK FOOTER //
// ./src/components/page/process/ProcessConverter.js